import { useCallback, useState } from 'react';

import { useAuthStore } from '@/stores/auth-store';
import { formatRelativeTime } from '@/utils/format-time';
import { validateReply } from '@/utils/validation';

import type { InlineCommentData } from './inline-comment';

export interface CommentThreadProps {
  /** The root comment of the thread */
  rootComment: InlineCommentData;
  /** Flat list of replies to the root comment */
  replies: InlineCommentData[];
  /** Called when a reply is submitted */
  onSubmitReply: (parentId: string, body: string) => Promise<void>;
  /** Called when the thread is resolved */
  onResolve: (commentId: string) => Promise<void>;
  /** Called when the thread is unresolved */
  onUnresolve: (commentId: string) => Promise<void>;
  /** Optional current time for relative time formatting (for testing) */
  now?: Date;
}

/**
 * Displays a comment thread with a root comment and flat replies.
 * Supports reply submission, resolve/unresolve toggling, and collapsed state for resolved threads.
 */
export function CommentThread({
  rootComment,
  replies,
  onSubmitReply,
  onResolve,
  onUnresolve,
  now,
}: CommentThreadProps) {
  const [isExpanded, setIsExpanded] = useState(!rootComment.resolved);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const user = useAuthStore((state) => state.user);

  const validation = validateReply(replyBody);
  const isSubmitDisabled = !validation.valid || isSubmitting;

  // Resolve button is visible for thread author or Admin
  const canResolve =
    user !== null &&
    (user.displayName === rootComment.author.displayName || user.role === 'Admin');

  const handleSubmitReply = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validation.valid) return;

      setIsSubmitting(true);
      setError(null);

      try {
        await onSubmitReply(rootComment.id, replyBody);
        setReplyBody('');
        setShowReplyForm(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit reply';
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [replyBody, rootComment.id, onSubmitReply, validation.valid],
  );

  const handleResolve = useCallback(async () => {
    setIsResolving(true);
    setResolveError(null);

    try {
      await onResolve(rootComment.id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve thread';
      setResolveError(message);
    } finally {
      setIsResolving(false);
    }
  }, [rootComment.id, onResolve]);

  const handleUnresolve = useCallback(async () => {
    setIsResolving(true);
    setResolveError(null);

    try {
      await onUnresolve(rootComment.id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to unresolve thread';
      setResolveError(message);
    } finally {
      setIsResolving(false);
    }
  }, [rootComment.id, onUnresolve]);

  const handleCancelReply = useCallback(() => {
    setShowReplyForm(false);
    setReplyBody('');
    setError(null);
  }, []);

  return (
    <div
      className={`rounded border ${
        rootComment.resolved
          ? 'border-success/30 bg-success/5'
          : 'border-border bg-surface'
      }`}
      data-testid={`comment-thread-${rootComment.id}`}
      aria-label={`Comment thread by ${rootComment.author.displayName}${rootComment.resolved ? ' (resolved)' : ''}`}
    >
      {/* Thread header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          {rootComment.resolved && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-content-tertiary hover:text-content"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse resolved thread' : 'Expand resolved thread'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {rootComment.resolved && (
            <span
              className="inline-flex rounded bg-success/15 px-1.5 py-0.5 text-xs font-medium text-success"
              data-testid="resolved-badge"
            >
              Resolved
            </span>
          )}
        </div>

        {/* Resolve/Unresolve button */}
        {canResolve && (
          <button
            type="button"
            onClick={rootComment.resolved ? handleUnresolve : handleResolve}
            disabled={isResolving}
            className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="resolve-button"
          >
            {isResolving
              ? 'Processing...'
              : rootComment.resolved
                ? 'Unresolve'
                : 'Resolve'}
          </button>
        )}
      </div>

      {/* Resolve error */}
      {resolveError && (
        <p
          className="px-4 py-1 text-xs text-danger"
          role="alert"
          data-testid="resolve-error"
        >
          {resolveError}
        </p>
      )}

      {/* Root comment - always visible */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content">
            {rootComment.author.displayName}
          </span>
          <span className="text-xs text-content-secondary">
            {formatRelativeTime(rootComment.createdAt, now)}
          </span>
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm text-content-secondary">
          {rootComment.body}
        </p>
      </div>

      {/* Expanded content: replies and reply form */}
      {(!rootComment.resolved || isExpanded) && (
        <>
          {/* Replies */}
          {replies.length > 0 && (
            <div className="border-t border-border-subtle" data-testid="replies-section">
              {replies.map((reply) => (
                <div
                  key={reply.id}
                  className="border-b border-border-subtle/50 px-4 py-2 pl-8 last:border-b-0"
                  data-testid={`reply-${reply.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-content">
                      {reply.author.displayName}
                    </span>
                    <span className="text-xs text-content-secondary">
                      {formatRelativeTime(reply.createdAt, now)}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-content-secondary">
                    {reply.body}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Reply button / form */}
          {!showReplyForm ? (
            <div className="border-t border-border-subtle px-4 py-2">
              <button
                type="button"
                onClick={() => setShowReplyForm(true)}
                className="text-xs font-medium text-brand-600 hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                data-testid="reply-button"
              >
                Reply
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmitReply}
              className="border-t border-border-subtle px-4 py-3"
              data-testid="reply-form"
              aria-label="Reply to comment"
            >
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Write a reply..."
                maxLength={10240}
                rows={2}
                className="w-full resize-y rounded border border-border bg-surface px-3 py-2 text-sm text-content placeholder-content-tertiary focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                aria-label="Reply body"
                disabled={isSubmitting}
              />

              {/* Validation message */}
              {replyBody.length > 0 && !validation.valid && validation.error && (
                <p className="mt-1 text-xs text-danger" role="alert">
                  {validation.error}
                </p>
              )}

              {/* Submission error */}
              {error && (
                <p
                  className="mt-1 text-xs text-danger"
                  role="alert"
                  data-testid="reply-error"
                >
                  {error}
                </p>
              )}

              <div className="mt-2 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-content-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="reply-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelReply}
                  disabled={isSubmitting}
                  className="rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="reply-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Counts resolved and unresolved threads from a list of comments.
 * A thread is identified by a root comment (one without a parentId).
 * Returns { resolved, unresolved } counts.
 */
export function countThreadStatus(comments: InlineCommentData[]): {
  resolved: number;
  unresolved: number;
} {
  let resolved = 0;
  let unresolved = 0;

  for (const comment of comments) {
    // Only count root comments (threads)
    if (!comment.parentId) {
      if (comment.resolved) {
        resolved++;
      } else {
        unresolved++;
      }
    }
  }

  return { resolved, unresolved };
}
